import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Card, Container, Form, Spinner } from 'react-bootstrap'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { RESOURCE_TYPES, fmtDateTimeTH } from './aoShared'

// ปฏิทินการจองห้อง/รถ — ครูทุกคนดูได้ (อนุมัติแล้วทั้งหมด + pending ของตนเอง)
export default function BookingCalendar() {
  const [bookings, setBookings] = useState([])
  const [resources, setResources] = useState([])
  const [rsId, setRsId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (rsId) params.set('rs_id', rsId)
      const [bkRes, rsRes] = await Promise.all([
        axiosInstance.get(`/api/ao/bookings/calendar?${params.toString()}`),
        resources.length ? Promise.resolve(null) : axiosInstance.get('/api/ao/resources'),
      ])
      if (bkRes.data.success) setBookings(bkRes.data.data)
      if (rsRes?.data.success) setResources(rsRes.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsId])

  useEffect(() => { fetchData() }, [fetchData])

  const events = bookings.map(b => ({
    id: String(b.bk_id),
    title: `${b.rs_name} — ${b.bk_purpose}`,
    start: b.bk_start,
    end: b.bk_end,
    backgroundColor: b.bk_status === 'pending' ? '#ffc107'
      : (b.rs_type === 'vehicle' ? '#fd7e14' : '#506fd9'),
    borderColor: 'transparent',
    extendedProps: b,
  }))

  const showEvent = (info) => {
    const b = info.event.extendedProps
    const rt = RESOURCE_TYPES[b.rs_type] || RESOURCE_TYPES.room
    Swal.fire({
      title: b.rs_name,
      html: `
        <div style="text-align:left;font-size:0.9rem">
          <div><b>ประเภท:</b> ${rt.label}</div>
          <div><b>ช่วงเวลา:</b> ${fmtDateTimeTH(b.bk_start)} - ${fmtDateTimeTH(b.bk_end)}</div>
          <div><b>ผู้จอง:</b> ${b.booked_by}</div>
          <div><b>วัตถุประสงค์:</b> ${b.bk_purpose}</div>
          ${b.bk_destination ? `<div><b>ปลายทาง:</b> ${b.bk_destination}</div>` : ''}
          <div><b>สถานะ:</b> ${b.bk_status === 'pending' ? 'รออนุมัติ (ของฉัน)' : 'อนุมัติแล้ว'}</div>
        </div>`,
      confirmButtonText: 'ปิด',
    })
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-calendar-line me-2 text-primary" />
              ปฏิทินการจองห้อง / รถ
            </h4>
            <div className="d-flex align-items-center gap-2">
              <Badge bg="primary">ห้องประชุม</Badge>
              <Badge bg="warning" text="dark">รถ/รออนุมัติ</Badge>
              <Form.Select size="sm" style={{ width: 220 }} value={rsId} onChange={e => setRsId(e.target.value)}>
                <option value="">ทุกทรัพยากร</option>
                {resources.map(r => (
                  <option key={r.rs_id} value={r.rs_id}>
                    [{RESOURCE_TYPES[r.rs_type]?.label || r.rs_type}] {r.rs_name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Body>
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : (
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{ start: 'prev,next today', center: 'title', end: 'dayGridMonth,timeGridWeek,timeGridDay' }}
                  locale="th"
                  buttonText={{ today: 'วันนี้', month: 'เดือน', week: 'สัปดาห์', day: 'วัน' }}
                  events={events}
                  eventClick={showEvent}
                  height="auto"
                  dayMaxEventRows={4}
                />
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </>
  )
}
